frappe.provide("erpnext.stock"); // @dokos

/**
 * Supply & Coverage panel. // @dokos
 *
 * The fulfilment twin of the Payment Collection panel: it answers, on the document itself,
 * "can I deliver this, when, and what do I do about it" — so a user can spot a shortage and fix
 * it without opening the Stock Projection report or reading Bin by hand.
 *
 * Same mechanics as payment_collection.js — a tab, HTML fields, ONE whitelisted context call,
 * Espresso tokens, real frappe.ui atoms. Different picture: payment is a lifecycle and gets a
 * stepper; coverage is a portfolio over time and gets a timeline, one row per line.
 *
 * Written for four callers from the start (Sales Order here, then Material Request, Purchase Order
 * and Work Order): everything below is driven by the context payload, not by the doctype.
 */

const COVERAGE_CONTEXT_METHOD = "erpnext.stock.projection.get_supply_coverage_context";

// Two vocabularies, one per mode. A demand document asks whether it is covered; a supply document
// asks whether anyone is waiting. The names never collide, so one map serves both.
// @dokos: translated with a context — the bare "Short" belongs to the shop floor ("Court")
const STATUS_LABEL = {
	// demand
	short: __("Short", null, "Supply Coverage"),
	at_risk: __("At risk", null, "Supply Coverage"),
	covered: __("Covered", null, "Supply Coverage"),
	// supply
	late: __("Late", null, "Supply Coverage"),
	tight: __("Tight", null, "Supply Coverage"),
	on_time: __("On time", null, "Supply Coverage"),
};

const MODE_SUPPLY = "supply";

// Order the count pills are shown in, per mode.
const COUNT_ORDER = {
	demand: ["covered", "at_risk", "short"],
	supply: ["on_time", "tight", "late"],
};

// @dokos: status → façade theme (erpnext.dokos.ui), so pills, banner and row labels speak the shared
// status vocabulary. Demand and supply share the same three tones.
const STATUS_THEME = {
	short: "red",
	at_risk: "amber",
	covered: "green",
	late: "red",
	tight: "amber",
	on_time: "green",
};
const BANNER_THEMES = ["red", "amber", "green"];

// A one-day gap on a six-month horizon is sub-pixel. Floor it so a real shortage stays visible;
// this overstates very short gaps slightly, which is the right way round to be wrong.
const MIN_SEGMENT_PERCENT = 0.4;

erpnext.stock.setup_supply_coverage = function (frm) {
	if (frm.is_new() || frm.doc.docstatus !== 1) return;
	if (!frm.get_field("dokos_supply_coverage_html")) return;

	// Hidden until we know there is something to show. An order of services, or one already
	// delivered, has nothing to cover — an empty tab is a promise the panel cannot keep, and the
	// user pays for it on every order by scanning a tab that never says anything.
	erpnext.stock.toggle_tab(frm, false);

	frappe.call({
		method: COVERAGE_CONTEXT_METHOD,
		args: { reference_doctype: frm.doctype, reference_name: frm.doc.name },
		callback: ({ message }) => erpnext.stock._render_coverage(frm, message),
	});
};

/**
 * Show or hide the whole tab.
 *
 * `set_df_property` refreshes the field control but not the tab link, so the Tab object has to be
 * driven directly — same reason, and same shape, as erpnext.proforma.toggle_tab on this doctype.
 */
erpnext.stock.toggle_tab = function (frm, show) {
	const tab = frm.get_field("dokos_supply_coverage_html")?.tab;
	if (!tab) {
		frm.set_df_property("dokos_supply_coverage_tab", "hidden", show ? 0 : 1);
		return;
	}

	// Never strand the user on a tab that is about to disappear.
	if (!show && frm.get_active_tab?.() === tab) {
		frm.layout?.tabs?.find((other) => other !== tab && !other.hidden)?.set_active();
	}

	tab.df.hidden = show ? 0 : 1;
	tab.toggle(show);
};

erpnext.stock._render_coverage = function (frm, context) {
	const field = frm.get_field("dokos_supply_coverage_html");
	if (!field || !field.$wrapper) return;

	// Nothing to cover: leave the tab hidden rather than showing an empty one.
	if (!context || !context.lines || !context.lines.length) {
		field.$wrapper.empty();
		erpnext.stock._render_lines(frm, context);
		erpnext.stock.toggle_tab(frm, false);
		return;
	}

	const $wrap = field.$wrapper.empty().addClass("sc-section");
	$wrap.append(erpnext.stock._render_headline(frm, context));
	$wrap.append(erpnext.stock._render_timeline(context));
	erpnext.stock._render_lines(frm, context);
	erpnext.stock.toggle_tab(frm, true);
};

// ---------------------------------------------------------------------------
// Zone 1 — the headline
// ---------------------------------------------------------------------------

// @dokos: the summary is an Espresso banner (subtle fill, no border) rather than a bordered box —
// a shortage is a warning to act on, not an error dialog. Counts and actions ride in its footer.
erpnext.stock._render_headline = function (frm, context) {
	const theme = BANNER_THEMES.includes(context.tone) ? context.tone : "green";

	const $footer = $("<div class='sc-summary-footer'></div>");
	const counts = context.counts || {};
	const $counts = $("<div class='sc-counts'></div>").appendTo($footer);
	(COUNT_ORDER[context.mode] || COUNT_ORDER.demand).forEach((status) => {
		const count = counts[status];
		if (!count) return;
		$counts.append(
			erpnext.dokos.ui.pill({
				label: `${count} ${STATUS_LABEL[status]}`,
				theme: STATUS_THEME[status],
				// outline: a subtle pill would vanish into the banner of the same tone
				variant: "outline",
			})
		);
	});

	let sub = "";
	if (context.coverage_date) {
		// Same slot, opposite reading: on a supply document the date is when somebody started
		// waiting, not when cover arrives.
		sub =
			context.mode === MODE_SUPPLY
				? __("{0} has been waiting since {1}.", [
						context.blocking_item || "",
						frappe.datetime.str_to_user(context.coverage_date),
				  ])
				: __("Earliest full coverage {0} — held by {1}.", [
						frappe.datetime.str_to_user(context.coverage_date),
						context.blocking_item || "",
				  ]);
	}

	const $actions = $("<div class='sc-actions'></div>").appendTo($footer);
	(context.actions || []).forEach((action) => {
		$actions.append(
			erpnext.dokos.ui.button({
				label: action.label,
				variant: action.primary ? "solid" : "subtle",
				onclick: () => erpnext.stock._run_action(frm, action),
			})
		);
	});

	return erpnext.dokos.ui.alert({
		title: context.headline,
		description: sub,
		theme,
		css_class: "sc-summary",
		footer: $footer,
	});
};

// ---------------------------------------------------------------------------
// Zone 2 — the coverage timeline
// ---------------------------------------------------------------------------

erpnext.stock._render_timeline = function (context) {
	const scale = erpnext.stock._timeline_scale(context);
	if (!scale) return $();

	const $tl = $("<div class='sc-timeline'></div>");
	$tl.append(erpnext.stock._render_axis(scale));

	const $rows = $("<div class='sc-rows'></div>").appendTo($tl);
	context.lines.forEach((line) => $rows.append(erpnext.stock._render_row(line, scale, context.mode)));

	$tl.append(erpnext.stock._render_legend(context.mode));
	return $tl;
};

/** Day-zero and span shared by every row, so all bars use one axis. */
erpnext.stock._timeline_scale = function (context) {
	const days = (from, to) => frappe.datetime.get_day_diff(to, from);
	const start = context.from_date;

	let last = start;
	context.lines.forEach((line) => {
		[line.required_date, line.clears_on, line.covered_until].forEach((date) => {
			if (date && days(start, date) > days(start, last)) last = date;
		});
		(line.incoming || []).forEach((movement) => {
			if (movement.date && days(start, movement.date) > days(start, last)) last = movement.date;
		});
	});

	// Always show a little runway past the last event, otherwise the final marker sits on the edge.
	const span = Math.max(days(start, last), 1) * 1.08;
	return { start, span, pct: (date) => Math.max(0, Math.min(100, (days(start, date) / span) * 100)) };
};

erpnext.stock._render_axis = function (scale) {
	const $axis = $("<div class='sc-axis'></div>");
	const ticks = Math.min(6, Math.max(2, Math.round(scale.span / 7)));

	for (let i = 0; i <= ticks; i++) {
		const day = Math.round((scale.span * i) / ticks);
		const date = frappe.datetime.add_days(scale.start, day);
		const left = (day / scale.span) * 100;
		// @dokos: the last tick is right-aligned; drop an inner tick that would overlap it
		if (i > 0 && i < ticks && 100 - left < 16) continue;
		const cls = i === 0 ? "sc-first" : i === ticks ? "sc-last" : "";
		$axis.append(
			$(`<span class="${cls}" style="left:${left}%"></span>`).text(frappe.datetime.str_to_user(date))
		);
	}
	return $axis;
};

erpnext.stock._render_row = function (line, scale, mode) {
	const status = line.status || "covered";
	const $row = $(`<div class="sc-row sc-row-${status}"></div>`);

	$(`<span class="sc-label"></span>`)
		.text(line.item_code)
		.attr("title", `${line.item_code} — ${line.item_name || ""}`)
		.appendTo($row);

	const $track = $("<div class='sc-track'></div>").appendTo($row);
	$track
		.attr("role", "img")
		.attr(
			"aria-label",
			mode === MODE_SUPPLY
				? __("{0}: {1}, arrives {2}", [
						line.item_code,
						STATUS_LABEL[status],
						frappe.datetime.str_to_user(line.required_date),
				  ])
				: __("{0}: {1}, needed {2}", [
						line.item_code,
						STATUS_LABEL[status],
						frappe.datetime.str_to_user(line.required_date),
				  ])
		);

	erpnext.stock._render_segments(line, scale, mode).forEach(($segment) => $track.append($segment));

	(line.incoming || []).forEach((movement) => {
		$track.append(
			$("<span class='sc-dot'></span>")
				.css("left", `${scale.pct(movement.date)}%`)
				.attr(
					"title",
					__("{0} arriving {1}", [movement.qty, frappe.datetime.str_to_user(movement.date)])
				)
		);
	});

	$track.append($("<span class='sc-pin sc-now'></span>").css("left", "0%"));
	$track.append(
		$("<span class='sc-pin'></span>")
			.css("left", `${scale.pct(line.required_date)}%`)
			.attr(
				"title",
				mode === MODE_SUPPLY
					? __("Arrives {0}", [frappe.datetime.str_to_user(line.required_date)])
					: __("Needed {0}", [frappe.datetime.str_to_user(line.required_date)])
			)
	);

	// @dokos: row status text coloured through the shared status map
	$(`<span class="sc-status dk-ink-${STATUS_THEME[status] || "green"}"></span>`)
		.text(STATUS_LABEL[status])
		.appendTo($row);
	return $row;
};

/**
 * Covered stretch, then the uncovered gap, then covered again once supply lands.
 *
 * `covered_until` null means the position never goes negative in the window, which is a different
 * answer from "covered until today" and must not collapse into it.
 */
erpnext.stock._render_segments = function (line, scale, mode) {
	const segments = [];
	const seg = (cls, from, to) => {
		const width = Math.max(to - from, MIN_SEGMENT_PERCENT);
		return $(`<span class="${cls}"></span>`).css({ left: `${from}%`, width: `${width}%` });
	};

	// Supply line: green while stock lasts, hatched from the day it runs out until this delivery
	// lands, green after. The hatch is exactly how long somebody waits — same grammar as the
	// demand side, read from the other end.
	if (mode === MODE_SUPPLY) {
		if (!line.needed_by) {
			segments.push(seg("sc-seg-ok", 0, 100));
			return segments;
		}
		const startsWaiting = scale.pct(line.needed_by);
		const arrives = scale.pct(line.required_date);
		if (startsWaiting > 0) segments.push(seg("sc-seg-ok", 0, startsWaiting));
		segments.push(seg("sc-seg-gap", startsWaiting, arrives));
		if (arrives < 100) segments.push(seg("sc-seg-ok", arrives, 100));
		return segments;
	}

	if (!line.covered_until) {
		segments.push(seg("sc-seg-ok", 0, 100));
		return segments;
	}

	const runsOut = scale.pct(line.covered_until);
	const backAgain = line.clears_on ? scale.pct(line.clears_on) : 100;

	if (runsOut > 0) segments.push(seg("sc-seg-ok", 0, runsOut));
	segments.push(seg("sc-seg-gap", runsOut, backAgain));
	if (line.clears_on && backAgain < 100) segments.push(seg("sc-seg-ok", backAgain, 100));

	return segments;
};

// @dokos: shared dk-legend / dk-swatch primitives; every swatch is painted with the same tokens as
// the track mark it explains (see supply_coverage.scss).
erpnext.stock._render_legend = function (mode) {
	const supply = mode === MODE_SUPPLY;
	const $legend = $("<div class='dk-legend sc-legend'></div>");
	[
		// The bar means the mirror thing on each side, and so does its legend: on a supply document
		// the hatch is somebody waiting, and the pin is when the delivery lands.
		["sc-sw-ok", supply ? __("In stock") : __("Covered")],
		["sc-sw-gap", supply ? __("Waiting") : __("Not covered")],
		["sc-sw-pin", supply ? __("Arrives") : __("Required date")],
		["sc-sw-now", __("Today")],
		["sc-sw-dot", __("Supply lands")],
	]
		.filter(([cls]) => !(supply && cls === "sc-sw-dot"))
		.forEach(([cls, label]) => {
			$legend.append(
				$(`<span><i class="dk-swatch ${cls}"></i></span>`).append(document.createTextNode(label))
			);
		});
	return $legend;
};

// ---------------------------------------------------------------------------
// Zone 3 — the line detail, in a native collapsible section
// ---------------------------------------------------------------------------

erpnext.stock._render_lines = function (frm, context) {
	const field = frm.get_field("dokos_supply_coverage_lines");
	if (!field || !field.$wrapper) return;

	const $wrap = field.$wrapper.empty().addClass("sc-lines");
	const lines = (context && context.lines) || [];
	if (!lines.length) return;

	const supply = context.mode === MODE_SUPPLY;
	const columns = supply
		? [
				__("Item"),
				__("Ordered"),
				__("Arrives"),
				__("Needed"),
				__("Days late"),
				__("Waiting for"),
				__("Status"),
		  ]
		: [
				__("Item"),
				__("Required"),
				__("By"),
				__("On Hand"),
				__("Incoming"),
				__("Short"),
				__("Covered Until"),
				__("Status"),
		  ];

	// @dokos: shared dk-table primitive; numeric columns use dk-r
	const $table = $(`
		<div class="dk-table-wrap">
			<table class="dk-table">
				<thead><tr></tr></thead>
				<tbody></tbody>
			</table>
		</div>`);

	const $head = $table.find("thead tr");
	columns.forEach((label, index) => {
		// First column is the item link, last is the status pill; everything between is numeric.
		const numeric = index > 0 && index < columns.length - 1;
		$(`<th class="${numeric ? "dk-r" : ""}"></th>`)
			.text(label)
			.appendTo($head);
	});

	const $body = $table.find("tbody");
	const cell = ($tr, text) => $("<td class='dk-r'></td>").text(text).appendTo($tr); // @dokos

	lines.forEach((line) => {
		const $tr = $("<tr></tr>").appendTo($body);
		$("<td></td>").append(frappe.utils.get_form_link("Item", line.item_code, true)).appendTo($tr);

		if (supply) {
			cell($tr, format_number(line.required_qty));
			cell($tr, frappe.datetime.str_to_user(line.required_date));
			cell($tr, line.needed_by ? frappe.datetime.str_to_user(line.needed_by) : "—");
			cell($tr, line.days_late ? __("{0} days", [line.days_late]) : "—");
			$("<td class='dk-r'></td>").html(erpnext.stock._waiting_cell(line)).appendTo($tr);
		} else {
			cell($tr, format_number(line.required_qty));
			cell($tr, frappe.datetime.str_to_user(line.required_date));
			cell($tr, format_number(line.on_hand || 0));
			$("<td class='dk-r'></td>").html(erpnext.stock._incoming_cell(line)).appendTo($tr);
			cell($tr, line.short_qty ? format_number(line.short_qty) : "0");
			cell(
				$tr,
				line.covered_until ? frappe.datetime.str_to_user(line.covered_until) : __("beyond horizon")
			);
		}

		$("<td></td>")
			.append(
				erpnext.dokos.ui.pill({
					label: STATUS_LABEL[line.status],
					theme: STATUS_THEME[line.status],
				})
			)
			.appendTo($tr); // @dokos
	});

	$wrap.append($table);
};

/**
 * What is waiting on an incoming line — the documents, not just a count.
 *
 * A buyer about to chase a supplier needs to know what is riding on the call.
 */
erpnext.stock._waiting_cell = function (line) {
	const waiting = line.waiting || [];
	// @dokos: dk-muted replaces Bootstrap text-muted
	if (!waiting.length) return `<span class="dk-muted">${frappe.utils.escape_html(__("nobody"))}</span>`;

	return waiting
		.map((entry) => `${format_number(entry.qty)} · ${frappe.utils.escape_html(entry.voucher)}`)
		.join("<br>");
};

erpnext.stock._incoming_cell = function (line) {
	const incoming = line.incoming || [];
	if (!incoming.length) return `<span class="dk-muted">—</span>`; // @dokos

	return incoming
		.map(
			(movement) =>
				`${format_number(movement.qty)} · ${frappe.utils.escape_html(
					frappe.datetime.str_to_user(movement.date)
				)}`
		)
		.join("<br>");
};

// ---------------------------------------------------------------------------
// Zone 4 — the one functional action
// ---------------------------------------------------------------------------

/**
 * Re-reads coverage at the moment the button fires rather than trusting the payload the browser
 * has been holding, then hands the short lines to the doctype's own mapper. No new verbs.
 */
erpnext.stock._run_action = function (frm, action) {
	if (action.name !== "material_request") return;

	frappe.call({
		method: "erpnext.stock.projection.get_short_lines",
		args: { reference_doctype: frm.doctype, reference_name: frm.doc.name },
		freeze: true,
		freeze_message: __("Checking coverage…"),
		callback: ({ message }) => {
			if (!message || !message.length) {
				frappe.msgprint({
					title: __("Nothing to request"),
					message: __("Every line is covered by its required date."),
					indicator: "green",
				});
				return;
			}
			erpnext.stock._make_material_request(frm, action, message);
		},
	});
};

/**
 * Open a Material Request holding exactly the short lines.
 *
 * Deliberately not frappe.model.open_mapped_doc: that helper takes no callback, so there is no
 * point at which the mapped document can be trimmed before it is synced and routed to. Passing one
 * is silently ignored — the user gets every line of the order at full quantity, which is the
 * opposite of what this button promises.
 *
 * So this does what open_mapped_doc does — call the mapper, sync, route — with the one step it has
 * no room for in between: keep only what is short, at the quantity that is short, dated when it is
 * needed.
 */
erpnext.stock._make_material_request = function (frm, action, short_lines) {
	frappe.call({
		// POST so the mapper's own `method` argument travels in the body rather than the query
		// string, alongside the RPC's `cmd`. open_mapped_doc posts for the same reason.
		type: "POST",
		method: "frappe.model.mapper.make_mapped_doc",
		// The mapper comes from the action, not from a branch on frm.doctype: the panel stays
		// doctype-agnostic and adding a surface is a server-side entry, not a case here.
		args: { method: action.method, source_name: frm.doc.name },
		freeze: true,
		freeze_message: __("Creating Material Request…"),
		callback: ({ message }) => {
			if (!message) return;

			const wanted = new Map(short_lines.map((line) => [line.item_code, line]));
			message.items = (message.items || []).filter((row) => wanted.has(row.item_code));
			message.items.forEach((row) => {
				const line = wanted.get(row.item_code);
				row.qty = line.short_qty;
				row.stock_qty = line.short_qty;
				row.schedule_date = line.required_date;
				row.warehouse = line.warehouse || row.warehouse;
			});

			if (!message.items.length) {
				frappe.msgprint({
					title: __("Nothing to request"),
					message: __("The short lines are not requestable from this document."),
					indicator: "orange",
				});
				return;
			}

			frappe.model.sync(message);
			frappe.get_doc(message.doctype, message.name).__run_link_triggers = true;
			frappe.set_route("Form", message.doctype, message.name);
		},
	});
};

// The panel is doctype-agnostic; this list is the only thing that grows as surfaces are added.
// Which question each doctype gets — "am I covered" or "who is waiting" — is decided server-side
// by the coverage contract, not here.
["Sales Order", "Material Request", "Purchase Order", "Work Order"].forEach((doctype) => {
	frappe.ui.form.on(doctype, {
		refresh(frm) {
			erpnext.stock.setup_supply_coverage(frm);
		},
	});
});
